(() => {
  window.DawnStorefrontEvents = {
    start(target, name, payload) {
      const EventClass = window.StandardEvents?.[name];
      if (!EventClass) return null;
      let deferred;
      try {
        deferred = EventClass.createPromise();
        deferred.promise.catch(() => {});
        const event = new EventClass({ ...payload, promise: deferred.promise });
        event.promise.catch(() => {});
        target.dispatchEvent(event);
      } catch (error) {
        deferred?.reject(error);
        return null;
      }
      let settled = false;
      return {
        resolve(value) { if (!settled) { settled = true; deferred.resolve(value); } },
        reject(error) { if (!settled) { settled = true; deferred.reject(error); } },
      };
    },
  };
  const bulkRequests = new WeakMap();
  window.DawnCartEvents = {
    startBulk(target, items) {
      const request = {};
      bulkRequests.set(target, request);
      const metadata = target.querySelector("[data-bulk-cart-lines]");
      let cartLines = [];
      let validMetadata = true;
      try {
        cartLines = metadata ? JSON.parse(metadata.textContent) : [];
        validMetadata =
          Array.isArray(cartLines) &&
          cartLines.every(
            (line) =>
              line &&
              line.id &&
              Number.isInteger(line.quantity) &&
              line.quantity >= 0 &&
              /^\d+$/.test(String(line.variantId)),
          );
      } catch (error) {
        validMetadata = false;
      }
      if (!validMetadata) cartLines = [];
      const groups = {};
      for (const [variantId, quantity] of Object.entries(items)) {
        if (
          !/^\d+$/.test(variantId) ||
          !Number.isInteger(quantity) ||
          quantity < 0
        )
          continue;
        const input = target.querySelector(
          `[data-quantity-variant-id="${variantId}"]`,
        );
        const stored = cartLines.find(
          (line) => String(line.variantId) === variantId,
        );
        const key = stored?.id || input?.dataset.quantityLineKey;
        const current =
          stored?.quantity ??
          Number(
            input?.dataset.quantityLineQuantity ?? input?.dataset.cartQuantity,
          );
        if (
          !Number.isFinite(current) ||
          current === quantity ||
          (current > 0 && !key)
        )
          continue;
        const action =
          current === 0 ? "add" : quantity === 0 ? "remove" : "update";
        (groups[action] ||= []).push(
          action === "add"
            ? { merchandiseId: variantId, quantity }
            : { id: key, quantity },
        );
      }
      const operations = (validMetadata ? Object.entries(groups) : [])
        .map(([action, lines]) =>
          this.start(target, "CartLinesUpdateEvent", {
            action,
            context: target.closest("bulk-modal") ? "dialog" : "product",
            lines,
          }),
        )
        .filter(Boolean);
      // Queued changes can defer section rendering, including while the SDK is absent.
      const refreshMetadata = (cart) => {
        if (bulkRequests.get(target) !== request) return;
        if (
          Array.isArray(cart?.items) &&
          cart.items.every(
            (line) =>
              line &&
              line.key &&
              Number.isInteger(line.quantity) &&
              Number.isInteger(line.variant_id),
          )
        ) {
          if (metadata)
            metadata.textContent = JSON.stringify(
              cart.items.map((line) => ({
                variantId: line.variant_id,
                id: line.key,
                quantity: line.quantity,
              })),
            );
          target
            .querySelectorAll("[data-quantity-variant-id]")
            .forEach((input) => {
              const matches = cart.items.filter(
                (line) =>
                  String(line.variant_id) === input.dataset.quantityVariantId,
              );
              input.dataset.quantityLineKey = matches[0]?.key || "";
              input.dataset.quantityLineQuantity = matches[0]?.quantity || 0;
              input.dataset.cartQuantity = matches.reduce(
                (sum, line) => sum + line.quantity,
                0,
              );
            });
        }
      };
      return {
        resolve: (cart) => {
          operations.forEach((operation) => operation.resolve(cart));
          refreshMetadata(cart);
        },
        reject: (error) =>
          operations.forEach((operation) => operation.reject(error)),
        decline: async (message) => {
          if (!operations.length) return;
          try {
            const response = await fetch(`${routes.cart_url}.js`, {
              cache: "no-store",
            });
            if (!response.ok)
              throw new Error(`Cart request failed: ${response.status}`);
            const cart = await response.json();
            const userErrors = [
              {
                code: "INVALID",
                message:
                  typeof message === "string"
                    ? message
                    : JSON.stringify(message),
              },
            ];
            operations.forEach((operation) =>
              operation.resolve(cart, userErrors),
            );
            refreshMetadata(cart);
          } catch (error) {
            operations.forEach((operation) => operation.reject(error));
          }
        },
      };
    },
    start(target, eventName, payload) {
      const EventClass = window.StandardEvents?.[eventName];
      if (!EventClass) return null;
      let deferred;
      try {
        deferred = EventClass.createPromise();
        deferred.promise.catch(() => {});
        const event = new EventClass({ ...payload, promise: deferred.promise });
        event.promise.catch(() => {});
        target.dispatchEvent(event);
      } catch (error) {
        deferred?.reject(error);
        console.warn('[Dawn] Cart event unavailable.', error);
        return null;
      }
      const { CartErrorEvent } = window.StandardEvents;
      const ancestor = target.closest('cart-drawer, quick-add-modal, product-info');
      let settled = false;
      const reject = (error) => {
        if (settled) return;
        settled = true;
        deferred.reject(error);
        if (CartErrorEvent) {
          const errorTarget = target.isConnected ? target : ancestor?.isConnected ? ancestor : document;
          try {
            errorTarget.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'SERVICE_UNAVAILABLE' }));
          } catch (eventError) {
            console.warn('[Dawn] Cart error event unavailable.', eventError);
          }
        }
      };
      const resolve = (cart, userErrors = []) => Promise.resolve().then(async () => {
        if (cart === undefined) {
          const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          cart = await response.json();
        }
        if (!cart?.token || !cart.currency || !Number.isFinite(cart.item_count) ||
            !Number.isFinite(cart.total_price) || !Array.isArray(cart.items) ||
            cart.items.some((line) => !line.key || !Number.isFinite(line.quantity) || !Number.isFinite(line.final_line_price))) {
          throw new Error('Incomplete cart event response');
        }
        const result = { cart: EventClass.createCartFromAjaxResponse(cart) };
        if (userErrors.length) result.userErrors = userErrors;
        if (!settled) {
          settled = true;
          deferred.resolve(result);
        }
      }).catch(reject);
      return {
        resolve,
        reject,
        decline: (message) => resolve(undefined, [{ code: 'INVALID', message: typeof message === 'string' ? message : JSON.stringify(message) }]),
      };
    },
  };
  const initialize = (events) => {
    window.StandardEvents = events;
    if (!customElements.get('collection-component')) {
      customElements.define('collection-component', events.createViewEventElement());
    }
    if (!customElements.get('product-component')) {
      customElements.define('product-component', events.createViewEventElement(HTMLElement, { defaultTrigger: 'intersect' }));
    }
    document.dispatchEvent(new Event('lc:standard-events-ready'));
    const pageView = () => document.dispatchEvent(new events.PageViewEvent({
      page: {
        template: document.querySelector('main[data-template]')?.dataset.template || '',
        title: document.title,
        url: window.location.href,
      },
    }));
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', pageView, { once: true });
    } else {
      pageView();
    }
  };
  if (window.StandardEvents) {
    initialize(window.StandardEvents);
  } else {
    import('https://cdn.shopify.com/storefront/standard-events.js')
      .then(initialize)
      .catch((error) => console.warn('[Dawn] Standard events unavailable.', error));
  }
})();
